import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Anchor, Badge, Table, TableBody, TableCell, TableProvider, TableRow } from '@percent/lemonade'
import { getAssigneeTypeLabel } from '@percent/domain/iam'
import type { InternalServiceRoleHolder } from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'
import { useAssigneeHref } from '@percent/admin-dashboard/app/accounts/hooks'
import { RowActionsMenu } from '@percent/admin-dashboard/common/components/rowActionsMenu/RowActionsMenu'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { holderLabel, isLastOwner, useServiceRoleLabels } from './roles'

type ServiceRolesTableProps = {
  holders: InternalServiceRoleHolder[]
  isLoading: boolean
  canSetRoles: boolean
  onChangeRole: (holder: InternalServiceRoleHolder) => void
  onRevoke: (holder: InternalServiceRoleHolder) => void
}

export function ServiceRolesTable({
  holders,
  isLoading,
  canSetRoles,
  onChangeRole,
  onRevoke
}: Readonly<ServiceRolesTableProps>) {
  const { t } = useTranslation()
  const hrefOf = useAssigneeHref()
  const { labelOf } = useServiceRoleLabels()

  const columns = [
    { id: 'name', content: t('typography.name'), props: { width: '35%' } },
    { id: 'type', content: t('typography.type'), props: { width: '15%' } },
    { id: 'role', content: t('typography.role'), props: { width: '15%' } },
    { id: 'granted', content: t('serviceDetail.roles.table.granted'), props: { width: '30%' } },
    { id: 'actions', content: '', props: { width: '5%' } }
  ]

  const renderHolder = (holder: InternalServiceRoleHolder) => {
    const testId = `service-role-holder-${holder.holderId}`
    // A holder with no name can no longer hold a role, so there is no page to open
    const href = holder.holderName ? hrefOf({ type: holder.holderType, id: holder.holderId }) : undefined

    return href ? (
      <Anchor component={Link} to={href} data-testid={testId}>
        {holder.holderName}
      </Anchor>
    ) : (
      <span data-testid={testId}>{holderLabel(holder)}</span>
    )
  }

  return (
    <TableProvider
      columns={columns}
      totalResults={holders.length}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={t('serviceDetail.roles.emptyTable.title')}
      emptyTableDescription={t('serviceDetail.roles.emptyTable.description')}
    >
      <Table>
        <TableBody>
          {holders.map(holder => {
            const lastOwner = isLastOwner(holders, holder)
            const lastOwnerReason = lastOwner ? t('internalServices.error.lastOwner') : undefined

            return (
              <TableRow key={holder.id} data-testid={`service-role-row-${holder.holderId}`}>
                <TableCell>{renderHolder(holder)}</TableCell>
                <TableCell>{getAssigneeTypeLabel(holder.holderType, t)}</TableCell>
                <TableCell>
                  <Badge variant="default" testId={`service-role-badge-${holder.holderId}`}>
                    {labelOf(holder.role)}
                  </Badge>
                </TableCell>
                <TableCell>{getDate(holder.createdAt)}</TableCell>
                <TableCell>
                  <RowActionsMenu
                    label={t('rolesAndPermissions.menu.actions', { name: holderLabel(holder) })}
                    dataTestId={`service-role-menu-${holder.holderId}`}
                    actions={[
                      {
                        key: 'change',
                        label: t('serviceDetail.roles.menu.changeRole'),
                        onClick: () => onChangeRole(holder),
                        dataTestId: `service-role-change-${holder.holderId}`,
                        isVisible: canSetRoles,
                        disabledReason: lastOwnerReason
                      },
                      {
                        key: 'revoke',
                        label: t('button.remove'),
                        onClick: () => onRevoke(holder),
                        dataTestId: `service-role-revoke-${holder.holderId}`,
                        isVisible: canSetRoles,
                        disabledReason: lastOwnerReason
                      }
                    ]}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableProvider>
  )
}
